import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import TeamModal from '../components/TeamModal';
import TeamDetails from '../components/TeamDetails';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiTrendingUp, FiCalendar, FiPlus, FiEdit3, FiTrash2, FiEye, FiLogOut, FiActivity } = FiIcons;

const Dashboard = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication and load user data
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const userData = localStorage.getItem('user');
    
    if (!isAuthenticated || !userData) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    loadUserTeams(parsedUser.id);
  }, [navigate]);

  const loadUserTeams = (userId) => {
    try {
      // Get all teams from localStorage
      const allTeams = JSON.parse(localStorage.getItem('teams') || '[]');
      
      // Filter teams that belong to this user
      const userTeams = allTeams.filter(team => team.ownerId === userId);
      
      // If user has no teams, create default teams for them
      if (userTeams.length === 0) {
        const defaultTeams = createDefaultTeams(userId);
        setTeams(defaultTeams);
        saveTeamsToStorage(defaultTeams);
      } else {
        setTeams(userTeams);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      // Create default teams as fallback
      const defaultTeams = createDefaultTeams(userId);
      setTeams(defaultTeams);
      saveTeamsToStorage(defaultTeams);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTeams = (userId) => {
    return [
      {
        id: Date.now(),
        name: 'Frontend Development Team',
        description: 'Responsible for user interface development and user experience',
        members: ['Alice Johnson', 'Bob Smith', 'Carol White'],
        ownerId: userId,
        createdAt: new Date().toISOString(),
        lastAssessment: null,
        overallScore: 0,
        trend: 'stable',
        status: 'Not Assessed',
        assessmentHistory: []
      },
      {
        id: Date.now() + 1,
        name: 'Backend Infrastructure Team',
        description: 'Manages server-side architecture and database systems',
        members: ['David Brown', 'Eve Davis', 'Frank Miller'],
        ownerId: userId,
        createdAt: new Date().toISOString(),
        lastAssessment: null,
        overallScore: 0,
        trend: 'stable',
        status: 'Not Assessed',
        assessmentHistory: []
      },
      {
        id: Date.now() + 2,
        name: 'DevOps Team',
        description: 'Handles deployment pipelines and infrastructure automation',
        members: ['Grace Lee', 'Henry Wilson'],
        ownerId: userId,
        createdAt: new Date().toISOString(),
        lastAssessment: null,
        overallScore: 0,
        trend: 'stable',
        status: 'Not Assessed',
        assessmentHistory: []
      }
    ];
  };

  const saveTeamsToStorage = (teamsToSave) => {
    try {
      // Get all existing teams
      const allTeams = JSON.parse(localStorage.getItem('teams') || '[]');
      
      // Remove this user's existing teams
      const otherTeams = allTeams.filter(team => team.ownerId !== user.id);
      
      // Combine with updated teams
      const updatedAllTeams = [...otherTeams, ...teamsToSave];
      
      localStorage.setItem('teams', JSON.stringify(updatedAllTeams));
    } catch (error) {
      console.error('Error saving teams:', error);
    }
  };

  const handleCreateTeam = () => {
    setCurrentTeam(null);
    setIsModalOpen(true);
  };

  const handleEditTeam = (team) => {
    setCurrentTeam(team);
    setIsModalOpen(true);
    setIsDetailsOpen(false);
  };

  const handleSaveTeam = (teamData) => {
    let updatedTeams;
    
    if (currentTeam) {
      // Update existing team
      updatedTeams = teams.map(team => 
        team.id === teamData.id ? { ...teamData, ownerId: user.id } : team
      );
    } else {
      // Create new team
      const newTeam = {
        ...teamData,
        id: Date.now(),
        ownerId: user.id,
        createdAt: new Date().toISOString(),
        lastAssessment: null,
        overallScore: 0,
        trend: 'stable',
        status: 'Not Assessed',
        assessmentHistory: []
      };
      updatedTeams = [...teams, newTeam];
    }
    
    setTeams(updatedTeams);
    saveTeamsToStorage(updatedTeams);
    setIsModalOpen(false);
    setCurrentTeam(null);
  };

  const handleDeleteTeam = (teamId) => {
    if (window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      const updatedTeams = teams.filter(team => team.id !== teamId);
      setTeams(updatedTeams);
      saveTeamsToStorage(updatedTeams);
      setIsDetailsOpen(false);
      setSelectedTeam(null);
    }
  };

  const handleViewTeam = (team) => {
    setSelectedTeam(team);
    setIsDetailsOpen(true);
  };

  const handleStartAssessment = (team) => {
    // Set current team for assessment
    localStorage.setItem('currentTeam', JSON.stringify({
      id: team.id,
      name: team.name,
      ownerId: team.ownerId
    }));
    navigate('/assessment');
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Excellent': return 'green';
      case 'Good': return 'blue';
      case 'Fair': return 'yellow';
      default: return 'red';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return FiTrendingUp;
      case 'down': return FiTrendingUp;
      default: return FiTrendingUp;
    }
  };

  const averageScore = teams.length > 0 
    ? Math.round(teams.reduce((sum, team) => sum + (team.overallScore || 0), 0) / teams.length)
    : 0;

  const totalMembers = teams.reduce((sum, team) => sum + (team.members?.length || 0), 0);

  const assessedTeams = teams.filter(team => team.lastAssessment).length;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-red-600">Please log in to view your dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user.name}</p>
          <p className="text-sm text-gray-500 mt-1">Company: {user.company || 'Not specified'}</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreateTeam}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            <span>Add Team</span>
          </button>
          <button 
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            <SafeIcon icon={FiLogOut} className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Teams</p>
              <p className="text-3xl font-bold text-gray-900">{teams.length}</p>
            </div>
            <SafeIcon icon={FiUsers} className="h-12 w-12 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Members</p>
              <p className="text-3xl font-bold text-gray-900">{totalMembers}</p>
            </div>
            <SafeIcon icon={FiUsers} className="h-12 w-12 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Score</p>
              <p className="text-3xl font-bold text-gray-900">{averageScore}%</p>
            </div>
            <SafeIcon icon={FiTrendingUp} className="h-12 w-12 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Assessed</p>
              <p className="text-3xl font-bold text-gray-900">{assessedTeams}</p>
            </div>
            <SafeIcon icon={FiActivity} className="h-12 w-12 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Your Teams</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Assessment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teams.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{team.name}</div>
                    {team.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{team.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <SafeIcon icon={FiUsers} className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{team.members?.length || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {team.lastAssessment ? new Date(team.lastAssessment).toLocaleDateString() : 'Not assessed'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-semibold text-gray-900">{team.overallScore || 0}%</span>
                      {team.trend && team.trend !== 'stable' && (
                        <SafeIcon 
                          icon={getTrendIcon(team.trend)} 
                          className={`h-4 w-4 ml-2 ${team.trend === 'up' ? 'text-green-500' : 'text-red-500 rotate-180'}`} 
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-${getStatusColor(team.status)}-100 text-${getStatusColor(team.status)}-800`}>
                      {team.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleStartAssessment(team)}
                        className="text-green-600 hover:text-green-900" 
                        title="Start Assessment"
                      >
                        <SafeIcon icon={FiActivity} className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleViewTeam(team)} 
                        className="text-blue-600 hover:text-blue-900" 
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditTeam(team)} 
                        className="text-gray-600 hover:text-gray-900" 
                        title="Edit Team"
                      >
                        <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteTeam(team.id)} 
                        className="text-red-600 hover:text-red-900" 
                        title="Delete Team"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {teams.length === 0 && (
            <div className="text-center py-12">
              <SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No teams yet</h3>
              <p className="text-gray-600 mb-4">Create your first team to get started with psychological safety assessments.</p>
              <button 
                onClick={handleCreateTeam}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
                Create Team
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h2>
        <div className="space-y-4">
          {teams
            .filter(team => team.assessmentHistory && team.assessmentHistory.length > 0)
            .flatMap(team => 
              team.assessmentHistory.slice(-3).map(assessment => ({
                team: team.name,
                action: `completed assessment with ${assessment.overallScore}% score`,
                time: new Date(assessment.date).toLocaleDateString()
              }))
            )
            .slice(0, 5)
            .map((activity, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-600 rounded-full" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.team}</span> {activity.action}
                  </p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          
          {teams.filter(team => team.assessmentHistory && team.assessmentHistory.length > 0).length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No recent activity. Start an assessment to see activity here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TeamModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        team={currentTeam} 
        onSave={handleSaveTeam} 
      />
      <TeamDetails 
        team={selectedTeam} 
        onClose={() => setIsDetailsOpen(false)} 
        onEdit={handleEditTeam} 
        onDelete={handleDeleteTeam} 
        onStartAssessment={handleStartAssessment} 
      />
    </div>
  );
};

export default Dashboard;